var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Vapi from '@vapi-ai/web';
import UIEventBus from '../EventBus';
const VAPI_PUBLIC_KEY = process.env.VAPI_PUBLIC_KEY || '';
const VAPI_ASSISTANT_ID = process.env.VAPI_ASSISTANT_ID || '';
const ROCKY_IMAGE = '/models/Agent/rocky.png';
const SUGGESTED_QUESTIONS = [
    'Ask: "What\'s Sai\'s best AI project?"',
    'Ask: "Tell me about Sai\'s experience"',
    'Ask: "What is Sai studying at SJSU?"',
    'Ask: "How did Sai build this portfolio?"',
    'Ask: "Is Sai open to new roles?"',
];
/** Pull Rocky's second-brain knowledge from the backend (Supermemory-powered). */
function fetchAgentKnowledge(timeoutMs = 4000) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const res = yield fetch('/api/agent-context', {
                signal: controller.signal,
            });
            if (!res.ok)
                return null;
            const data = (yield res.json());
            return (_a = data.knowledge) !== null && _a !== void 0 ? _a : null;
        }
        catch (_b) {
            return null;
        }
        finally {
            clearTimeout(timer);
        }
    });
}
const PortfolioCat = () => {
    const [phase, setPhase] = useState('idle');
    const [statusText, setStatusText] = useState('');
    const [isCallActive, setIsCallActive] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [volumeLevel, setVolumeLevel] = useState(0);
    const [showBubble, setShowBubble] = useState(false);
    const [hintIdx, setHintIdx] = useState(0);
    const vapiRef = useRef(null);
    const bubbleTimerRef = useRef();
    const vapiConfigured = Boolean(VAPI_PUBLIC_KEY && VAPI_ASSISTANT_ID);
    const setPhaseAndNotify = useCallback((p) => {
        setPhase(p);
        UIEventBus.dispatch('agentVoicePhase', p);
    }, []);
    const showStatus = useCallback((text, autohide = false) => {
        setStatusText(text);
        setShowBubble(true);
        clearTimeout(bubbleTimerRef.current);
        if (autohide) {
            bubbleTimerRef.current = setTimeout(() => setShowBubble(false), 2500);
        }
    }, []);
    useEffect(() => {
        if (!vapiConfigured)
            return;
        vapiRef.current = new Vapi(VAPI_PUBLIC_KEY);
        vapiRef.current.on('call-start', () => {
            setIsCallActive(true);
            setIsLoading(false);
            setPhaseAndNotify('listening');
            showStatus('AMAZE! Ask about Sai!', true);
        });
        vapiRef.current.on('call-end', () => {
            setIsCallActive(false);
            setIsLoading(false);
            setVolumeLevel(0);
            setPhaseAndNotify('idle');
            showStatus('Click me to chat!', true);
        });
        vapiRef.current.on('call-start-failed', (event) => {
            var _a;
            setIsCallActive(false);
            setIsLoading(false);
            setPhaseAndNotify('idle');
            const msg = (_a = event.error) !== null && _a !== void 0 ? _a : 'Call failed to start';
            console.error('[Vapi] call-start-failed:', event);
            showStatus(msg, true);
        });
        // mid-call errors only (startup failures are handled by call-start-failed)
        vapiRef.current.on('error', (err) => {
            var _a, _b;
            const e = err;
            if ((e === null || e === void 0 ? void 0 : e.type) === 'start-method-error')
                return; // already handled by call-start-failed
            setIsCallActive(false);
            setIsLoading(false);
            setVolumeLevel(0);
            setPhaseAndNotify('idle');
            const msg = (_b = (_a = e === null || e === void 0 ? void 0 : e.error) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : (typeof err === 'string' ? err : 'Voice error');
            console.error('[Vapi] error:', err);
            showStatus(msg, true);
        });
        vapiRef.current.on('volume-level', (level) => {
            setVolumeLevel(level);
        });
        return () => {
            var _a;
            (_a = vapiRef.current) === null || _a === void 0 ? void 0 : _a.stop();
            vapiRef.current = null;
        };
    }, [vapiConfigured, setPhaseAndNotify, showStatus]);
    useEffect(() => {
        if (!isCallActive)
            return;
        if (volumeLevel > 0.12) {
            setPhaseAndNotify('speaking');
        }
        else {
            setPhaseAndNotify('listening');
        }
    }, [isCallActive, volumeLevel, setPhaseAndNotify]);
    const toggleCall = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!vapiConfigured || isLoading)
            return;
        if (isCallActive) {
            (_a = vapiRef.current) === null || _a === void 0 ? void 0 : _a.stop();
            return;
        }
        setIsLoading(true);
        setPhaseAndNotify('processing');
        showStatus('Waking up my brain...');
        try {
            // Load Rocky's second brain (Supermemory) and inject it into the call.
            // If the backend is unreachable, start anyway — Rocky still talks.
            const knowledge = yield fetchAgentKnowledge();
            showStatus('Connecting...');
            yield ((_b = vapiRef.current) === null || _b === void 0 ? void 0 : _b.start(VAPI_ASSISTANT_ID, knowledge ? { variableValues: { knowledge } } : undefined));
            // success/failure handled by call-start, call-start-failed, error events
        }
        catch (err) {
            setIsLoading(false);
            setPhaseAndNotify('idle');
            const msg = err instanceof Error ? err.message : 'Failed to start';
            console.error('[Vapi] start threw:', err);
            showStatus(msg, true);
        }
    }), [
        vapiConfigured,
        isLoading,
        isCallActive,
        setPhaseAndNotify,
        showStatus,
    ]);
    useEffect(() => {
        const open = () => toggleCall();
        UIEventBus.on('openVoiceChat', open);
        return () => UIEventBus.remove('openVoiceChat', open);
    }, [toggleCall]);
    // Rotate suggested questions while idle
    useEffect(() => {
        if (isCallActive || isLoading)
            return;
        const t = setInterval(() => setHintIdx((i) => (i + 1) % SUGGESTED_QUESTIONS.length), 6000);
        return () => clearInterval(t);
    }, [isCallActive, isLoading]);
    const phaseClass = phase === 'speaking'
        ? 'rocky-speaking'
        : phase === 'listening'
            ? 'rocky-listening'
            : phase === 'processing'
                ? 'rocky-processing'
                : '';
    return ReactDOM.createPortal(React.createElement(React.Fragment, null,
        React.createElement("style", null, `
                @keyframes rockyFloat {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-10px); }
                }
                @keyframes rockyWiggle {
                    0%, 100% { transform: translateY(0px) rotate(-2deg); }
                    50% { transform: translateY(-8px) rotate(2deg); }
                }
                @keyframes rockyPulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.07); }
                }
                @keyframes rockyBounce {
                    0%, 100% { transform: translateY(0px) scaleY(1); }
                    40% { transform: translateY(-12px) scaleY(0.95); }
                    70% { transform: translateY(-4px) scaleY(1.02); }
                }
                @keyframes bubblePop {
                    from { opacity: 0; transform: translateY(6px) scale(0.9); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes waveBar {
                    0%, 100% { transform: scaleY(0.4); }
                    50%      { transform: scaleY(1); }
                }
                .rocky-wrap {
                    position: fixed;
                    right: 24px;
                    bottom: 24px;
                    z-index: 100000;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }
                .rocky-bubble {
                    background: rgba(10,10,10,0.88);
                    border: 1.5px solid #f4a261;
                    color: #fff;
                    font-family: monospace;
                    font-size: 11px;
                    padding: 5px 10px;
                    border-radius: 8px;
                    margin-bottom: 8px;
                    white-space: nowrap;
                    animation: bubblePop 0.2s ease-out;
                    pointer-events: none;
                    max-width: 200px;
                    text-align: center;
                    white-space: normal;
                }
                .rocky-wave {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 3px;
                    height: 20px;
                    margin-bottom: 4px;
                }
                .rocky-wave span {
                    display: block;
                    width: 4px;
                    border-radius: 2px;
                    background: #f4a261;
                    transform-origin: center;
                }
                .rocky-btn {
                    padding: 0;
                    border: none;
                    background: transparent;
                    cursor: pointer;
                    animation: rockyFloat 3.2s ease-in-out infinite;
                    filter: drop-shadow(0 8px 24px rgba(0,0,0,0.5));
                    transition: filter 0.2s ease;
                }
                .rocky-btn:hover {
                    filter: drop-shadow(0 12px 28px rgba(244,162,97,0.6));
                    animation-play-state: paused;
                }
                .rocky-btn img {
                    display: block;
                    width: clamp(110px, 16vw, 160px);
                    height: auto;
                    pointer-events: none;
                    user-select: none;
                }
                .rocky-btn.rocky-listening {
                    animation: rockyWiggle 1.4s ease-in-out infinite;
                }
                .rocky-btn.rocky-processing {
                    animation: rockyBounce 1s ease-in-out infinite;
                }
                .rocky-btn.rocky-speaking {
                    animation: rockyPulse 0.55s ease-in-out infinite;
                }
                .rocky-hint {
                    display: block;
                    font-family: monospace;
                    font-size: 10px;
                    color: #f4a261;
                    text-align: center;
                    opacity: 0.8;
                    margin-top: 2px;
                    pointer-events: none;
                }
                .rocky-suggestion {
                    color: #ddd;
                    opacity: 0.65;
                    max-width: 180px;
                    animation: bubblePop 0.4s ease-out;
                }
            `),
        React.createElement("div", { className: "rocky-wrap" },
            showBubble && React.createElement("div", { className: "rocky-bubble" }, statusText),
            (phase === 'listening' || phase === 'speaking') && (React.createElement("div", { className: "rocky-wave" }, [...Array(5)].map((_, i) => {
                const h = phase === 'speaking' ? 6 + volumeLevel * 28 : 8;
                return (React.createElement("span", { key: i, style: {
                        height: `${h}px`,
                        background: phase === 'speaking'
                            ? '#2a9d8f'
                            : '#e63946',
                        animation: `waveBar ${0.5 + i * 0.1}s ease-in-out infinite`,
                        animationDelay: `${i * 0.08}s`,
                    } }));
            }))),
            React.createElement("button", { type: "button", className: `rocky-btn ${phaseClass}`, onClick: toggleCall, disabled: isLoading, "aria-label": "Talk to Rocky", title: isCallActive
                    ? 'Click to end call'
                    : "Click Rocky to chat about Sai's portfolio" },
                React.createElement("img", { src: ROCKY_IMAGE, alt: "Rocky", draggable: false })),
            React.createElement("span", { className: "rocky-hint" }, isLoading
                ? '⏳ connecting...'
                : isCallActive
                    ? '🔴 tap to end'
                    : '🎙️ tap to chat'),
            !isCallActive && !isLoading && (React.createElement("span", { className: "rocky-hint rocky-suggestion" }, SUGGESTED_QUESTIONS[hintIdx])))), document.body);
};
export default PortfolioCat;
